import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { cn } from '../utils/cn';

export interface ThumbnailProps {
  /** Optional remote image URL. Empty/null falls back to an initials tile. */
  src?: string | null;
  /** Display label (e.g. product name). First 1-2 letters become the initials. */
  name?: string | null;
  /**
   * Stable seed that determines the fallback colour. Pass a row id (e.g.
   * `productId`) so the same row keeps the same colour across renders.
   * Defaults to `name` when omitted.
   */
  seed?: string | null;
  /** Override default size (h-9 w-9). */
  className?: string;
  /** Alt text for screen readers — defaults to `name`. */
  alt?: string;
  /** Rendered when both `src` is empty and `name` is empty. */
  fallback?: React.ReactNode;
}

// Tailwind-safe palette of background classes. The hash → index pick is
// stable so the same seed always renders the same colour.
const PALETTE = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-fuchsia-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-lime-600',
  'bg-cyan-600',
  'bg-violet-500',
  'bg-pink-500',
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/**
 * Square thumbnail with three rendering tiers:
 *
 * 1. Valid `src` → render the image.
 * 2. `src` failed to load OR is empty + `name` provided → coloured initials
 *    tile with a hash-stable colour (so the same product keeps the same
 *    colour across reloads / pagination).
 * 3. No `name` either → `<ImageOff />` glyph (or whatever `fallback` is set).
 *
 * Used by the panel All Products / detail modal so admins see a useful
 * placeholder instead of a broken-image icon when `imageUrl` is null.
 */
export function Thumbnail({
  src,
  name,
  seed,
  className,
  alt,
  fallback,
}: ThumbnailProps) {
  const [broken, setBroken] = useState(false);
  const trimmed = (src ?? '').trim();
  const showImg = trimmed.length > 0 && !broken;

  const label = (name ?? '').trim();
  const showInitials = !showImg && label.length > 0;

  const colour = (() => {
    const key = (seed ?? label ?? '').trim();
    if (!key) return PALETTE[0]!;
    return PALETTE[hashString(key) % PALETTE.length]!;
  })();

  return (
    <div
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-card-hover',
        className,
      )}
    >
      {showImg ? (
        <img
          src={trimmed}
          alt={alt ?? label}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : showInitials ? (
        <span
          className={cn(
            'flex h-full w-full items-center justify-center text-xs font-bold tracking-wide text-white',
            colour,
          )}
          aria-label={alt ?? label}
        >
          {initialsOf(label)}
        </span>
      ) : (
        fallback ?? <ImageOff className="h-4 w-4 text-content-muted" />
      )}
    </div>
  );
}

export default Thumbnail;
